import { useEffect, useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { getAdminSignupsSources, type AdminSignupsSourcePoint } from '@/lib/api/admin'

type Range = '7d' | '30d' | '90d'

export function SignupsAnalytics() {
  const [range, setRange] = useState<Range>('30d')
  const [data, setData] = useState<AdminSignupsSourcePoint[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    getAdminSignupsSources(range)
      .then((res) => {
        if (cancelled) return
        setData(res.items ?? [])
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load signups analytics')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [range])

  const totals = useMemo(() => {
    return data.reduce(
      (acc, row) => {
        acc.app += row.app
        acc.website += row.website
        acc.social += row.social
        acc.other += row.other
        return acc
      },
      { app: 0, website: 0, social: 0, other: 0 },
    )
  }, [data])

  const totalAll = totals.app + totals.website + totals.social + totals.other

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Signups analytics</h1>
          <p className="mt-1 text-sm text-gray-400">
            Understand where new viewers and creators are coming from across app, web, and social.
          </p>
        </div>
        <div className="inline-flex items-center gap-2 text-xs">
          <span className="text-gray-400">Range:</span>
          <div className="inline-flex rounded-full bg-white/5 p-1">
            {(['7d', '30d', '90d'] as Range[]).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setRange(value)}
                className={`px-3 py-1 rounded-full ${
                  range === value ? 'bg-[#D4AF37] text-black' : 'text-gray-300 hover:bg-white/5'
                }`}
              >
                {value === '7d' ? 'Last 7 days' : value === '30d' ? 'Last 30 days' : 'Last 90 days'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-700 bg-red-900/30 px-3 py-2 text-xs text-red-200">
          {error}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black px-4 py-4">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            Total signups ({range})
          </p>
          <p className="text-2xl font-semibold text-white mb-1">
            {loading && !data.length ? '—' : totalAll.toLocaleString()}
          </p>
          <p className="text-xs text-gray-400">
            New viewer and creator accounts created in the selected window.
          </p>
        </div>
        <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black px-4 py-4">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">From app</p>
          <p className="text-2xl font-semibold text-white mb-1">
            {loading && !data.length ? '—' : totals.app.toLocaleString()}
          </p>
          <p className="text-xs text-gray-400">
            Signups where source looks like app / mobile (Android, iOS).
          </p>
        </div>
        <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black px-4 py-4">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">From website</p>
          <p className="text-2xl font-semibold text-white mb-1">
            {loading && !data.length ? '—' : totals.website.toLocaleString()}
          </p>
          <p className="text-xs text-gray-400">
            Signups that appear to come via web or marketing landing pages.
          </p>
        </div>
        <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black px-4 py-4">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">From social</p>
          <p className="text-2xl font-semibold text-white mb-1">
            {loading && !data.length ? '—' : totals.social.toLocaleString()}
          </p>
          <p className="text-xs text-gray-400">
            Signups attributed to social sources like Instagram, X, Facebook, TikTok.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black p-4">
        <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">
          Signups by source over time
        </p>
        {loading && data.length === 0 ? (
          <div className="h-72 flex items-center justify-center text-sm text-gray-400">
            Loading signups chart…
          </div>
        ) : data.length === 0 ? (
          <div className="h-72 flex items-center justify-center text-sm text-gray-400">
            No signup data for this period.
          </div>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="day" stroke="#6b7280" fontSize={11} />
                <YAxis stroke="#6b7280" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#020617',
                    borderRadius: 8,
                    borderColor: '#111827',
                    fontSize: 12,
                  }}
                />
                <Legend />
                <Bar dataKey="app" stackId="sources" fill="#38BDF8" name="App" />
                <Bar dataKey="website" stackId="sources" fill="#22C55E" name="Website" />
                <Bar dataKey="social" stackId="sources" fill="#F97373" name="Social" />
                <Bar dataKey="other" stackId="sources" fill="#A855F7" name="Other" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black p-4">
        <p className="text-xs uppercase tracking-wide text-gray-500 mb-3">
          Source breakdown (approximate)
        </p>
        <div className="grid gap-3 md:grid-cols-4 text-xs">
          {[
            { key: 'app', label: 'App', value: totals.app },
            { key: 'website', label: 'Website', value: totals.website },
            { key: 'social', label: 'Social', value: totals.social },
            { key: 'other', label: 'Other / unknown', value: totals.other },
          ].map((row) => {
            const pct = totalAll > 0 ? Math.round((row.value / totalAll) * 100) : 0
            return (
              <div key={row.key} className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">{row.label}</span>
                  <span className="text-gray-400">
                    {row.value.toLocaleString()} ({pct}%)
                  </span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-gray-900 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-[#D4AF37]"
                    style={{ width: `${Math.max(4, pct)}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

